import {
    ChangeEvent,
    useEffect,
    useRef,
    useState,
} from 'react';
import { Textarea } from '../../../components/TextArea';
import { TypesButton } from '../../../constants/ButtonTypes';
import { SizesButton } from '../../../constants/SizeButton';
import { resolvePromotionArticles } from '../../../api/sales/getPromotions';
import { ResolvedArticleProduct } from '../../../types/Endpoints/sales/PromitionsContracts';
import { normalizeArticles } from './normalizeArticles';
import * as S from './styles';

type ArticlesBlockProps = {
  value: string[];
  onChange: (articles: string[]) => void;
};

export const ArticlesBlock = ({ value, onChange }: ArticlesBlockProps) => {
    const [inputValue, setInputValue] = useState(() => value.join(', '));
    const [found, setFound] = useState<ResolvedArticleProduct[]>([]);
    const [notFound, setNotFound] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const didAutoResolve = useRef(false);

    const resolveArticles = async (articles: string[]) => {
        if (!articles.length) {
            setFound([]);
            setNotFound([]);
            return;
        }

        setIsLoading(true);
        try {
            const result = await resolvePromotionArticles(articles);
            setFound(result.found);
            setNotFound(result.notFound);
        } catch {
            setFound([]);
            setNotFound(articles);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (didAutoResolve.current || !value.length) {
            return;
        }
        didAutoResolve.current = true;
        resolveArticles(value);
    }, [value]);

    const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        const nextValue = e.target.value;
        setInputValue(nextValue);
        onChange(normalizeArticles(nextValue));
    };

    const handleResolve = async () => {
        const articles = normalizeArticles(inputValue);
        setInputValue(articles.join(', '));
        onChange(articles);
        await resolveArticles(articles);
    };

    return (
        <S.Block>
            <S.Title>Артикул</S.Title>
            <S.Hint>
                Введите один или несколько артикулов через запятую. Товары добавляются к выборке поверх фильтров.
            </S.Hint>
            <Textarea
                name="articles"
                title="Список артикулов"
                rows={3}
                value={inputValue}
                onChange={handleInputChange}
            />
            <S.Actions>
                <S.ResolveButton
                    typeButton={TypesButton.SECONDARY}
                    size={SizesButton.M}
                    onClick={handleResolve}
                    disabled={isLoading}
                >
                    {isLoading ? 'Поиск...' : 'Подобрать'}
                </S.ResolveButton>
            </S.Actions>

            {!!found.length && (
                <S.Section>
                    <S.SectionTitle>
                        Найденные товары (
                        {found.length}
                        )
                    </S.SectionTitle>
                    {found.map(product => (
                        <S.FoundItem key={product.id}>
                            {product.article}
                            {' — '}
                            {product.fullName || product.name || `ID ${product.id}`}
                        </S.FoundItem>
                    ))}
                </S.Section>
            )}

            {!!notFound.length && (
                <S.Section>
                    <S.SectionTitle>Ненайденные артикулы</S.SectionTitle>
                    {notFound.map(article => (
                        <S.NotFoundItem key={article}>
                            {`Артикул ${article} не найден`}
                        </S.NotFoundItem>
                    ))}
                </S.Section>
            )}
        </S.Block>
    );
};
